import { useState } from "react";
import { MapPin } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Badge } from "../../components/shared/Badge";
import { AttendanceOverrideModal } from "../../components/shared/AttendanceOverrideModal";
import { useAppData } from "../../lib/AppDataContext";
import { ATTENDANCE_STATUS_META, formatDurationMs, isAutoAbsent } from "../../lib/attendance";

/* Today's Roster — admin view. Every salesperson, today's clock-in/out
   selfies + locations + total hours, with Set/Override action per row. */
export function TodaysRosterView() {
  const { allSalesPeople, attendanceLog, markAttendance, officeTiming } = useAppData();
  // Exclude users deactivated in Settings > Users (users.active) and
  // super_admins (platform owners, not attendance-tracked staff).
  const activeSalesPeople = allSalesPeople.filter(
    (m) => m.userActive !== false && m.role !== "super_admin"
  );
  const todayKey = new Date().toISOString().split("T")[0];
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [preview, setPreview] = useState(null);

  // Roll today's per-person records into a headcount. Same lookup the
  // table rows use, so the strip can never disagree with what's below it.
  // "Not marked" is its own bucket: no record is an absence of data, not
  // an attendance status, and shouldn't be folded into Absent.
  const rosterCounts = activeSalesPeople.reduce((acc, m) => {
    const rec = (attendanceLog[m.name] || []).find((r) => r.date === todayKey);
    const key = rec?.status || "not_marked";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const ROSTER_CARDS = [
    { key: "present",    label: "Present",    tone: "success", text: "text-emerald-700" },
    { key: "half_day",   label: "Half Day",   tone: "warning", text: "text-amber-700" },
    { key: "absent",     label: "Absent",     tone: "danger",  text: "text-red-700" },
    { key: "leave",      label: "Leave",      tone: "info",    text: "text-blue-700" },
    { key: "paid_leave", label: "Paid Leave", tone: "violet",  text: "text-violet-700" },
    { key: "not_marked", label: "Not marked", tone: "muted",   text: "text-slate-600" },
  ];

  return (
    <div>
      {preview && (
        <div className="fixed inset-0 z-[60] bg-slate-900/80 grid place-items-center p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="Selfie preview" className="max-w-[90vw] max-h-[85vh] rounded-lg ring-1 ring-white/20" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
      <PageHeader accent="ops"
        title="Attendance · Today's roster"
        subtitle={new Date().toLocaleDateString("en-IN", {
          weekday: "long", day: "numeric", month: "long", year: "numeric"
        })}
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        {ROSTER_CARDS.map((c) => (
          <Card key={c.key} tone={c.tone} className="p-4">
            <div className="text-xs font-medium text-slate-600">{c.label}</div>
            <div className={`text-2xl font-bold tabular-nums mt-1 ${c.text}`}>{rosterCounts[c.key] || 0}</div>
          </Card>
        ))}
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-slate-500 bg-slate-50 border-b border-slate-200">
                <th className="px-3 py-2.5">Employees</th>
                <th className="px-3 py-2.5">Status today</th>
                <th className="px-3 py-2.5">Clock-in</th>
                <th className="px-3 py-2.5">In location</th>
                <th className="px-3 py-2.5">In selfie</th>
                <th className="px-3 py-2.5">Clock-out</th>
                <th className="px-3 py-2.5">Out location</th>
                <th className="px-3 py-2.5">Out selfie</th>
                <th className="px-3 py-2.5">Total hours</th>
                <th className="px-3 py-2.5">Marked by</th>
                <th className="px-3 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {activeSalesPeople.map((m) => {
                const rec = (attendanceLog[m.name] || []).find((r) => r.date === todayKey);
                // Unmarked once the day has closed reads as Absent, matching
                // what payroll already counts.
                const auto = !rec && isAutoAbsent(todayKey, officeTiming?.weekendDay ?? 0, officeTiming?.endHHMM, m.profile?.joiningDate);
                const meta = rec ? ATTENDANCE_STATUS_META[rec.status] : (auto ? ATTENDANCE_STATUS_META.absent : null);
                const total = rec?.clockInAt && rec?.clockOutAt
                  ? formatDurationMs(new Date(rec.clockOutAt) - new Date(rec.clockInAt)) : null;
                const inSelfie = rec?.clockInSelfie || rec?.selfie;
                const inLocation = rec?.clockInLocation || rec?.location;
                return (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-900">{m.name}</div>
                      <div className="text-xs text-slate-500">{m.email}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      {meta ? (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded ring-1 ring-inset ${meta.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />{meta.label}
                        </span>
                      ) : <span className="text-xs text-slate-400">Not marked</span>}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-slate-600">
                      {rec?.clockInTime || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {inLocation ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <MapPin className="w-3 h-3 text-slate-400" />{inLocation}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {inSelfie ? (
                        <img src={inSelfie} alt="In selfie" onClick={() => setPreview(inSelfie)} className="w-8 h-8 rounded object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums text-slate-600">
                      {rec?.clockOutTime || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">
                      {rec?.clockOutLocation ? (
                        <span className="inline-flex items-center gap-1 text-xs">
                          <MapPin className="w-3 h-3 text-slate-400" />{rec.clockOutLocation}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {rec?.clockOutSelfie ? (
                        <img src={rec.clockOutSelfie} alt="Out selfie" onClick={() => setPreview(rec.clockOutSelfie)} className="w-8 h-8 rounded object-cover ring-1 ring-slate-200 cursor-pointer hover:ring-blue-400" />
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">
                      {total ? <span className="font-semibold text-blue-700">{total}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      {rec ? (
                        <Badge cls={rec.markedBy === "self"
                          ? "bg-slate-100 text-slate-600 ring-slate-400/20"
                          : "bg-blue-50 text-blue-700 ring-blue-600/20"}>
                          {rec.markedBy === "self" ? "Self" : "Admin"}
                        </Badge>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => setOverrideTarget({ name: m.name, date: todayKey, existing: rec })}
                        className="text-xs text-blue-600 font-medium hover:text-blue-800">
                        {rec ? "Override" : "Set"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <AttendanceOverrideModal
        target={overrideTarget}
        onMark={markAttendance}
        onClose={() => setOverrideTarget(null)}
      />
    </div>
  );
}
